<template>
  <div class="page-root">
    <header class="page-header">
      <MOPButton
        label="Dashboard"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm"
        @click="router.push('/dashboard')"
      />
      <div class="page-title-row">
        <i class="pi pi-file-edit page-title-icon"></i>
        <h1 class="page-title">Circulars</h1>
        <span class="page-count-badge">{{ filteredItems.length }} circulars</span>
        <MOPButton
          v-if="canCreate"
          label="New Circular"
          icon="pi pi-plus"
          class="create-btn"
          @click="router.push('/circulars/create')"
        />
      </div>
    </header>

    <!-- Filters -->
    <div class="filters-bar glass-card">
      <!-- Org / Dept tabs -->
      <div class="tab-row">
        <button class="tab-btn" :class="{ active: activeTab === 'org' }" @click="setTab('org')">
          <span class="tab-dot org"></span> Organization
          <span class="tab-count">{{ orgCirculars.length }}</span>
        </button>
        <button class="tab-btn" :class="{ active: activeTab === 'dept' }" @click="setTab('dept')">
          <span class="tab-dot dept"></span> Department
          <span class="tab-count">{{ deptCirculars.length }}</span>
        </button>
      </div>
      <div class="filter-divider"></div>

      <!-- Department dropdown — admin only, visible only on dept tab -->
      <div v-if="activeTab === 'dept' && ctx.isAdmin" class="filter-field filter-dept">
        <MOPDropdown
          v-model="selectedDeptId"
          label="Department"
          :options="deptOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All Departments"
          :showClear="true"
          :filter="true"
        />
      </div>

      <!-- Non-admin: show their department as a fixed tag -->
      <div v-else-if="activeTab === 'dept' && userDeptName" class="dept-fixed-tag">
        <i class="pi pi-building"></i>
        <span>{{ userDeptName }}</span>
      </div>

      <div class="filter-field">
        <MOPDatepicker
          v-model="dateFrom"
          label="From"
          dateFormat="dd/mm/yy"
          placeholder="Start date"
          :showButtonBar="true"
        />
      </div>
      <div class="filter-field">
        <MOPDatepicker
          v-model="dateTo"
          label="To"
          dateFormat="dd/mm/yy"
          placeholder="End date"
          :showButtonBar="true"
        />
      </div>
      <div class="filter-field filter-search">
        <MOPInputText
          v-model="searchTerm"
          label="Search"
          placeholder="Search circulars…"
          :clearable="true"
        />
      </div>
      <div class="filter-reset-col">
        <MOPButton
          label="Reset"
          icon="pi pi-refresh"
          class="p-button-outlined p-button-secondary p-button-sm"
          @click="resetFilters"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading || deptLoading" class="loading-state">
      <i class="pi pi-spin pi-spinner text-2xl text-teal-500"></i>
      <span>{{ deptLoading ? 'Loading department circulars…' : 'Loading circulars…' }}</span>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredItems.length === 0" class="empty-state glass-card">
      <i class="pi pi-inbox text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">No circulars found</p>
      <p class="text-slate-400 text-sm">Try adjusting the filters</p>
    </div>

    <!-- Circulars list -->
    <div v-else-if="!deptLoading" class="circulars-list">
      <div
        v-for="item in pagedItems"
        :key="item.Id"
        class="circular-card glass-card"
        :class="activeTab === 'org' ? 'card-org' : 'card-dept'"
        @click="router.push('/circulars/' + item.Id)"
      >
        <div class="circular-card-header">
          <div class="circular-type-indicator" :class="activeTab === 'org' ? 'type-org' : 'type-dept'">
            {{ activeTab === 'org' ? 'Organization' : 'Department' }}
          </div>
          <div class="header-right">
            <span class="circular-date">{{ formatDate(item.PublishedDate || item.CreatedDate) }}</span>
            <span v-if="!item.IsActive" class="inactive-badge">Inactive</span>
            <span v-if="item.VisibilityFrom || item.VisibilityTo" class="visibility-badge">
              <i class="pi pi-eye text-xs"></i>
              {{ formatVisibility(item) }}
            </span>
          </div>
        </div>

        <h3 class="circular-title">{{ item.Title }}</h3>
        <p v-if="item.Content" class="circular-content">{{ item.Content }}</p>

        <div class="circular-footer">
          <span v-if="item.DepartmentName" class="dept-name-tag">
            <i class="pi pi-building text-xs"></i> {{ item.DepartmentName }}
          </span>
          <div class="card-actions" @click.stop>
            <a v-if="item.FullAttachmentPath || item.AttachmentPath"
               :href="item.FullAttachmentPath || item.AttachmentPath"
               target="_blank" class="action-btn attach-btn" title="Download">
              <i class="pi pi-paperclip"></i>
            </a>
            <template v-if="canManage">
              <button class="action-btn edit-btn" title="Edit"
                @click="router.push('/circulars/' + item.Id + '/edit')">
                <i class="pi pi-pencil"></i>
              </button>
              <button class="action-btn toggle-btn"
                :class="item.IsActive ? 'active-on' : 'active-off'"
                :title="item.IsActive ? 'Deactivate' : 'Activate'"
                @click="toggleActive(item)">
                <i :class="item.IsActive ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
              </button>
              <button class="action-btn delete-btn" title="Delete"
                @click="confirmDelete(item)">
                <i class="pi pi-trash"></i>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-row">
      <MOPButton icon="pi pi-chevron-left" class="p-button-outlined p-button-secondary p-button-sm" :disabled="currentPage === 1" @click="currentPage--" />
      <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
      <MOPButton icon="pi pi-chevron-right" class="p-button-outlined p-button-secondary p-button-sm" :disabled="currentPage === totalPages" @click="currentPage++" />
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="confirm-overlay" @click.self="deleteTarget = null">
      <div class="confirm-dialog">
        <div class="confirm-icon"><i class="pi pi-exclamation-triangle"></i></div>
        <h3 class="confirm-title">Delete Circular?</h3>
        <p class="confirm-body">"<strong>{{ deleteTarget.Title }}</strong>" will be permanently removed.</p>
        <div class="confirm-actions">
          <MOPButton label="Cancel" class="p-button-outlined p-button-secondary" @click="deleteTarget = null" />
          <MOPButton label="Delete" icon="pi pi-trash" severity="danger" :loading="deleteInProgress" @click="doDelete" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import moment from 'moment'
import {
  getOrgCirculars, getDeptCirculars,
  activateCircular, deleteCircular,
  getDashboardUserContext, getDepartmentsByOrg
} from '../../services/dashboardService'
import { useCreatePermission } from '@/composables/useCreatePermission'

const router = useRouter()
const ctx = getDashboardUserContext()
const canManage = ctx.canManageSOP
// Create is gated by the role's "Create" operation on the Circulars entity.
const { canCreate, resolve: resolveCreatePermission } = useCreatePermission(
  (name) => name.includes('circular')
)

const loading = ref(true)
const orgCirculars = ref<any[]>([])
const deptCirculars = ref<any[]>([])
const activeTab = ref<'org' | 'dept'>('org')
const searchTerm = ref('')
const dateFrom = ref<Date | null>(null)
const dateTo = ref<Date | null>(null)
const currentPage = ref(1)
const PAGE_SIZE = 12

// Department filter
const deptOptions = ref<{ label: string; value: number }[]>([])
// Admin: dropdown selection (null = all). Non-admin: fixed to their dept
const selectedDeptId = ref<number | null>(ctx.isAdmin ? null : (ctx.departmentId || null))
const userDeptName = ref('')

const deleteTarget = ref<any>(null)
const deleteInProgress = ref(false)
const deptLoading = ref(false)

function setTab(tab: 'org' | 'dept') { activeTab.value = tab; currentPage.value = 1 }

// When admin selects a department → call API with that deptId
watch(selectedDeptId, async (deptId) => {
  deptLoading.value = true
  currentPage.value = 1
  deptCirculars.value = await getDeptCirculars(ctx.orgId, ctx.branchId, deptId ?? 0, ctx.isAdmin)
  deptLoading.value = false
})

const sourceItems = computed(() => activeTab.value === 'org' ? orgCirculars.value : deptCirculars.value)

const filteredItems = computed(() => {
  let items = sourceItems.value

  if (searchTerm.value.trim()) {
    const term = searchTerm.value.toLowerCase()
    items = items.filter(c =>
      (c.Title ?? '').toLowerCase().includes(term) ||
      (c.Content ?? '').toLowerCase().includes(term)
    )
  }
  if (dateFrom.value) {
    const from = moment(dateFrom.value).startOf('day')
    items = items.filter(c => {
      const d = moment(c.PublishedDate || c.CreatedDate)
      return d.isValid() ? d.isSameOrAfter(from, 'day') : true
    })
  }
  if (dateTo.value) {
    const to = moment(dateTo.value).endOf('day')
    items = items.filter(c => {
      const d = moment(c.PublishedDate || c.CreatedDate)
      return d.isValid() ? d.isSameOrBefore(to, 'day') : true
    })
  }
  return items
})

const totalPages = computed(() => Math.ceil(filteredItems.value.length / PAGE_SIZE) || 1)
const pagedItems = computed(() => {
  return filteredItems.value
})

watch([searchTerm, dateFrom, dateTo, activeTab], () => { currentPage.value = 1 })

function formatDate(d: any) {
  if (!d) return ''
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD MMM YYYY') : moment(d).format('DD MMM YYYY')
}

function formatVisibility(item: any) {
  const from = item.VisibilityFrom ? moment(item.VisibilityFrom).format('DD MMM YY') : null
  const to = item.VisibilityTo ? moment(item.VisibilityTo).format('DD MMM YY') : null
  if (from && to) return `${from} – ${to}`
  if (from) return `From ${from}`
  if (to) return `Until ${to}`
  return ''
}

function resetFilters() {
  searchTerm.value = ''
  dateFrom.value = null
  dateTo.value = null
  currentPage.value = 1
  // Admin: clear dept filter. Non-admin: keep their dept
  if (ctx.isAdmin) selectedDeptId.value = null
}

async function toggleActive(item: any) {
  const newStatus = !item.IsActive
  const ok = await activateCircular(item.Id, newStatus, ctx.userId)
  if (ok) item.IsActive = newStatus
}

function confirmDelete(item: any) { deleteTarget.value = item }

async function doDelete() {
  if (!deleteTarget.value) return
  deleteInProgress.value = true
  const ok = await deleteCircular(deleteTarget.value.Id, ctx.userId)
  if (ok) {
    orgCirculars.value = orgCirculars.value.filter(c => c.Id !== deleteTarget.value.Id)
    deptCirculars.value = deptCirculars.value.filter(c => c.Id !== deleteTarget.value.Id)
  }
  deleteInProgress.value = false
  deleteTarget.value = null
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') deleteTarget.value = null
}

onMounted(async () => {
  resolveCreatePermission()
  const initialDeptId = selectedDeptId.value ?? 0
  const [org, dept, depts] = await Promise.all([
    getOrgCirculars(ctx.orgId, ctx.branchId, ctx.isAdmin),
    getDeptCirculars(ctx.orgId, ctx.branchId, initialDeptId, ctx.isAdmin),
    getDepartmentsByOrg(ctx.orgId)
  ])
  orgCirculars.value = org
  deptCirculars.value = dept

  // Build dept options for admin dropdown
  deptOptions.value = depts.map((d: any) => ({ label: d.Name, value: d.ID }))

  // For non-admin: resolve their department name to display as a fixed tag
  if (!ctx.isAdmin && ctx.departmentId) {
    const match = depts.find((d: any) => d.ID === ctx.departmentId)
    userDeptName.value = match?.Name ?? ''
  }

  loading.value = false
  document.addEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.page-root { min-height: 100vh; background: #f1f5f9; padding: 1.5rem; font-family: 'Inter', sans-serif; }
.page-header { margin-bottom: 1.25rem; }
.page-title-row { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.25rem; }
.page-title-icon { font-size: 1.4rem; color: #0d9488; }
.page-title { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin: 0; }
.page-count-badge { background: #ccfbf1; color: #0d9488; border-radius: 20px; padding: 2px 10px; font-size: 0.78rem; font-weight: 600; }
.create-btn { margin-left: auto; }

.glass-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }

.filters-bar { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end; padding: 1rem 1.25rem; margin-bottom: 1.25rem; }
.tab-row { display: flex; gap: 8px; align-self: flex-end; padding-bottom: 2px; }
.tab-btn { display: flex; align-items: center; gap: 6px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; padding: 7px 14px; font-size: 0.85rem; color: #64748b; cursor: pointer; transition: all 0.15s; }
.tab-btn.active { background: #f0fdfa; border-color: #5eead4; color: #0d9488; font-weight: 600; }
.tab-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.tab-dot.org { background: #3b82f6; }
.tab-dot.dept { background: #10b981; }
.tab-count { background: #e2e8f0; border-radius: 10px; padding: 0 7px; font-size: 0.75rem; font-weight: 600; color: #64748b; }
.tab-btn.active .tab-count { background: #ccfbf1; color: #0d9488; }
.filter-divider { width: 1px; background: #e2e8f0; align-self: stretch; margin: 0 4px; }
.filter-field { min-width: 160px; }
.filter-dept { min-width: 200px; }
.filter-search { flex: 1; min-width: 220px; }
.filter-reset-col { align-self: flex-end; padding-bottom: 2px; }

/* Non-admin dept fixed tag */
.dept-fixed-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
  padding: 7px 14px;
  background: #f0fdfa;
  border: 1px solid #5eead4;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0d9488;
  margin-bottom: 2px;
}

.loading-state { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 3rem; color: #64748b; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; text-align: center; }

.circulars-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(380px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
.circular-card { padding: 1.25rem; cursor: pointer; transition: box-shadow 0.15s, border-color 0.15s; }
.circular-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.card-org:hover { border-color: #93c5fd; }
.card-dept:hover { border-color: #6ee7b7; }

.circular-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; flex-wrap: wrap; gap: 6px; }
.header-right { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.circular-type-indicator { border-radius: 12px; padding: 3px 12px; font-size: 0.72rem; font-weight: 600; }
.type-org { background: #dbeafe; color: #1d4ed8; }
.type-dept { background: #d1fae5; color: #065f46; }
.circular-date { font-size: 0.78rem; color: #94a3b8; }
.inactive-badge { background: #fef3c7; color: #d97706; border-radius: 10px; padding: 2px 8px; font-size: 0.7rem; font-weight: 600; }
.visibility-badge { display: inline-flex; align-items: center; gap: 3px; background: #f0f9ff; color: #0369a1; border-radius: 10px; padding: 2px 8px; font-size: 0.7rem; font-weight: 500; }
.circular-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 0 0 0.5rem 0; line-height: 1.4; }
.circular-content { font-size: 0.85rem; color: #475569; line-height: 1.6; margin: 0 0 0.75rem 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.circular-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 0.625rem; }
.dept-name-tag { display: inline-flex; align-items: center; gap: 4px; font-size: 0.78rem; color: #64748b; background: #f1f5f9; padding: 3px 10px; border-radius: 10px; }
.card-actions { display: flex; align-items: center; gap: 5px; margin-left: auto; }
.action-btn { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 7px; border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b; cursor: pointer; font-size: 0.75rem; text-decoration: none; transition: all 0.15s; }
.attach-btn { border-color: #5eead4; background: #f0fdfa; color: #0d9488; }
.attach-btn:hover { background: #ccfbf1; }
.edit-btn:hover { background: #eff6ff; color: #2563eb; border-color: #93c5fd; }
.toggle-btn.active-on { background: #f0fdf4; color: #16a34a; border-color: #86efac; }
.toggle-btn.active-on:hover { background: #dcfce7; }
.toggle-btn.active-off { background: #fff7ed; color: #d97706; border-color: #fcd34d; }
.toggle-btn.active-off:hover { background: #fef3c7; }
.delete-btn:hover { background: #fef2f2; color: #dc2626; border-color: #fca5a5; }

.pagination-row { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1rem 0; }
.page-info { font-size: 0.85rem; color: #64748b; }

.confirm-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; z-index: 1100; }
.confirm-dialog { background: #fff; border-radius: 14px; padding: 2rem; max-width: 420px; width: 90%; box-shadow: 0 20px 50px rgba(0,0,0,0.2); text-align: center; }
.confirm-icon { font-size: 2.5rem; color: #f59e0b; margin-bottom: 0.75rem; }
.confirm-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin: 0 0 0.5rem 0; }
.confirm-body { font-size: 0.875rem; color: #64748b; line-height: 1.6; margin-bottom: 1.5rem; }
.confirm-actions { display: flex; gap: 0.75rem; justify-content: center; }
</style>
